import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormControl,
  FormsModule,
  NgForm,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
  ButtonComponent,
  ButtonIconComponent,
  DialogComponent,
  safeDetectChanges,
} from '@blaze-shared/shared-ui';

import {
  ApiBuilderTestParameterDefinition,
  AssignParametersTestValuesDialogData,
} from '../api-builder.model';
import { ApiBuilderService } from '../services/api-builder.service';
import { TypedValueInputFieldComponent } from '../typed-value-input-field/typed-value-input-field.component';

@UntilDestroy()
@Component({
  selector: 'blaze-assign-parameters-test-values-dialog',
  imports: [
    ButtonComponent,
    ButtonIconComponent,
    DialogComponent,
    FormsModule,
    MatIconModule,
    ReactiveFormsModule,
    TypedValueInputFieldComponent,
  ],
  templateUrl: './assign-parameters-test-values-dialog.component.html',
  styleUrls: ['./assign-parameters-test-values-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignParametersTestValuesDialogComponent implements OnInit {
  private readonly apiBuilderService = inject(ApiBuilderService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialogRef =
    inject<MatDialogRef<AssignParametersTestValuesDialogComponent>>(MatDialogRef);

  @ViewChild('assignForm') assignForm!: NgForm;
  parametersDef: ApiBuilderTestParameterDefinition[] = [];
  formCtrl!: FormArray<FormControl<string>>;

  constructor() {
    const dialogData = inject<AssignParametersTestValuesDialogData>(MAT_DIALOG_DATA);

    this.parametersDef = dialogData.parameters;
  }

  ngOnInit(): void {
    this.formCtrl = new FormArray<FormControl<string>>(
      this.parametersDef.map((param) => {
        const value = param.dataType
          ? this.apiBuilderService.serializeParameterDefaultValue(param.defaultValue ?? param.value)
          : param.value;

        return new FormControl(value, {
          nonNullable: true,
          validators: !param.optional ? Validators.required : null,
        });
      }),
    );

    this.formCtrl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      safeDetectChanges(this.cdr);
    });
  }

  save(): void {
    if (this.formCtrl.invalid) {
      return;
    }

    const parameters: ApiBuilderTestParameterDefinition[] = this.parametersDef.map(
      (param, index) => {
        const formFieldValue = this.formCtrl.value[index];
        let value;

        if (param.dataType) {
          value = this.apiBuilderService.deserializeParameterDefaultValue(
            formFieldValue,
            param.dataType,
          );
        } else {
          try {
            value = JSON.parse(this.formCtrl.value[index]);
          } catch {
            value = this.formCtrl.value[index];
          }
        }

        return { ...param, value };
      },
    );

    this.dialogRef.close(parameters);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
